import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CheckCircle, Copy, MessageSquare } from 'lucide-react';
import { API_URL } from './bookingUtils';

export const SendLinkDialog = ({ open, onOpenChange, booking, onSent }) => {
  const [sendLinkData, setSendLinkData] = useState({ send_email: false, expiry_days: 14 });
  const [linkResult, setLinkResult] = useState(null);
  const [sending, setSending] = useState(false);

  const close = (value) => {
    if (!value) setLinkResult(null);
    onOpenChange(value);
  };

  const handleSend = async () => {
    if (!booking) return;
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/booking-forms/bookings/${booking.booking_id}/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...sendLinkData, origin: window.location.origin }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send link');
      }
      const result = await response.json();
      setLinkResult(result);
      toast.success('Link generated successfully');
      onSent?.(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (linkResult?.public_url) {
      navigator.clipboard.writeText(linkResult.public_url);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="send-link-dialog">
        <DialogHeader>
          <DialogTitle>Send Booking Link</DialogTitle>
          <DialogDescription>Generate and send the booking form link to the customer</DialogDescription>
        </DialogHeader>

        {linkResult ? (
          <div className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">Link generated successfully!</AlertDescription>
            </Alert>
            <div>
              <Label className="text-sm text-slate-500">Public Link</Label>
              <div className="flex gap-2 mt-1">
                <Input value={linkResult.public_url} readOnly className="flex-1 text-sm" data-testid="generated-public-link" />
                <Button variant="outline" size="icon" onClick={copyLink} data-testid="copy-generated-link-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Expires:</span>
              <span>{linkResult.expires_at && format(new Date(linkResult.expires_at), 'MMM d, yyyy')}</span>
            </div>
            {linkResult.email_sent && <p className="text-sm text-green-600">✓ Email sent to customer</p>}
            {linkResult.whatsapp_deeplink && (
              <Button variant="outline" className="w-full" asChild>
                <a href={linkResult.whatsapp_deeplink} target="_blank" rel="noopener noreferrer">
                  <MessageSquare className="w-4 h-4 mr-2" /> Open in WhatsApp
                </a>
              </Button>
            )}
            <Button onClick={() => close(false)} className="w-full" data-testid="send-link-done-btn">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Link Expiry (days)</Label>
              <Input type="number" value={sendLinkData.expiry_days} min={1} max={90} data-testid="link-expiry-input"
                onChange={(e) => setSendLinkData(prev => ({ ...prev, expiry_days: parseInt(e.target.value) || 14 }))} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="send-email" checked={sendLinkData.send_email} data-testid="send-email-checkbox"
                onChange={(e) => setSendLinkData(prev => ({ ...prev, send_email: e.target.checked }))} />
              <Label htmlFor="send-email">Send email notification to customer</Label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => close(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSend} disabled={sending} className="flex-1 bg-gold hover:bg-gold/90 text-slate-900" data-testid="generate-link-btn">
                {sending ? 'Generating...' : 'Generate Link'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
